<template>
  <div
    class="carousel"
  >
    <!-- 主图 -->
    <div class="main-swiper-container">
      <div
        @click="handleImageClick(activeIndex)"
        class="option-wrapper"
      >
        <div class="option translate">
          <Tooltip
            :offset="20"
            placement="left"
            content="Transalte"
          >
            <Icon
              @click.stop="handleTranslate"
              name="translate-l"
            />
          </Tooltip>
        </div>
        <div class="option find-similar">
          <Tooltip
            :offset="20"
            placement="left"
            content="Find Similar"
          >
            <Icon
              @click.stop="handleSimilar"
              name="icon-image-search-similar"
            />
          </Tooltip>
        </div>
        <div
          @click.stop="handlePrev"
          :class="{ disabled: activeIndex === 0 }"
          class="option button-prev"
        >
          <Icon name="icon-arrow-left-l" />
        </div>
        <div
          :class="{ disabled: activeIndex === swiperImages.length - 1 }"
          @click.stop="handleNext"
          class="option button-next"
        >
          <Icon name="arrow-right-l-2" />
        </div>
      </div>
      <Swiper
        class="main-swiper"
        :space-between="10"
        @swiper="onMainSwiperInit"
        @slide-change="onSlideChange"
      >
        <SwiperSlide
          v-for="(img, i) in swiperImages"
          :key="i"
        >
          <Image
            :src="img"
            class="main-img"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <!-- 缩略图 -->
    <div class="thumbs-container">
      <div
        @click="handlePrev"
        :class="{ disabled: activeIndex === 0 }"
        class="nav-btn prev"
      >
        <Icon name="icon-arrow-left-l" />
      </div>
      <div
        ref="thumbsRef"
        class="thumbs-list"
      >
        <div
          @mouseenter="handleThumbMouseenter(index)"
          v-for="(img, index) in swiperImages"
          :key="index"
          :class="{ active: index === activeIndex }"
          class="thumbs-item"
        >
          <Image
            :src="img"
            class="img"
          />
        </div>
      </div>
      <div
        @click="handleNext"
        :class="{ disabled: activeIndex === swiperImages.length - 1 }"
        class="nav-btn next"
      >
        <Icon name="arrow-right-l-2" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import Icon from '@/components/Icon/Icon.vue'
import Tooltip from '@/components/Tooltip/index.vue'
import { Image } from '@/components/Image'
import { useGoogleImageTranslate } from '@/hooks/useGoogleImageTranslate'
import { buriedPointUtil, openURLWithChannelQuery } from '@/utils/utils'
import { useRoute } from 'vue-router'
import { useImagePreview } from '../components/OfferImagePreview'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  swiperImages: {
    type: Array,
    default: () => []
  },
  productImageLength: {
    type: Number,
    default: 0
  }
})

const { open: openImagePreview } = useImagePreview()

const { translate } = useGoogleImageTranslate()

const route = useRoute()

const activeIndex = ref(0)

const mainSwiper = ref(null)

const thumbsRef = ref(null)

const offerId = computed(() => route.params.id)

const onMainSwiperInit = (swiper) => {
  mainSwiper.value = swiper
}

const onSlideChange = (swiper) => {
  activeIndex.value = swiper.realIndex
}

const handleSimilar = () => {
  buriedPointUtil({ eventId: 'POP201' })
  const imageUrl = props.swiperImages[activeIndex.value]
  sessionStorage.setItem(`currentMainImage_${offerId.value}`, imageUrl)
  openURLWithChannelQuery(`/search?offerId=${offerId.value}&type=similar`)
}

const handlePrev = () => {
  if (activeIndex.value === 0) return
  scrollThumbsIntoView(activeIndex.value - 1)
  mainSwiper.value.slidePrev()
}

const handleNext = () => {
  if (activeIndex.value === props.swiperImages.length - 1) return
  scrollThumbsIntoView(activeIndex.value + 1)
  mainSwiper.value.slideNext()
}

const scrollThumbsIntoView = (index) => {
  nextTick(() => {
    const thumbsEl = thumbsRef.value
    if (!thumbsEl) return

    const targetThumb = thumbsEl.children[index]
    if (!targetThumb) return

    const thumbsRect = thumbsEl.getBoundingClientRect()
    const targetRect = targetThumb.getBoundingClientRect()

    // 如果目标缩略图左侧超出容器左边界，则向左滚动
    if (targetRect.left < thumbsRect.left) {
      thumbsEl.scrollLeft -= (thumbsRect.left - targetRect.left + 5)
    }
    // 如果目标缩略图右侧超出容器右边界，则向右滚动
    else if (targetRect.right > thumbsRect.right) {
      thumbsEl.scrollLeft += (targetRect.right - thumbsRect.right + 5)
    }
  })
}

// 暴漏给外部调用，切换至目标sku图
const slideToSkuImage = (index) => {
  const _index = props.productImageLength + index
  mainSwiper.value.slideTo(_index)
  scrollThumbsIntoView(_index)
}

const handleThumbMouseenter = (index) => {
  mainSwiper.value.slideTo(index)
}

const handleTranslate = async () => {
  const image = props.swiperImages[activeIndex.value]
  console.log(props.title)
  translate(image, props.title)
}

const handleImageClick = (index) => {
  let type = ''
  let i = 0

  if (index < props.productImageLength) {
    type = 'main'
    i = index
  } else {
    type = 'variant'
    i = index - props.productImageLength
  }

  // main || variant
  openImagePreview(type, i)
}

onMounted(() => {
})

onUnmounted(() => {
})

defineExpose({
  slideToSkuImage
})

</script>

<style lang="less" scoped>
.carousel {
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: white;

  .main-swiper-container {
    position: relative;

    .option-wrapper {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 10;
      &:hover {
        .option {
          opacity: 1;
        }
      }
      .option {
        position: absolute;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        font-size: 20px;
        cursor: pointer;
        opacity: 0;
        transition: opacity .3s;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08),
              0 1px 3px rgba(0, 0, 0, 0.04);

        &.translate {
          top: 16px;
          right: 16px;
        }

        &.find-similar {
          top: 64px;
          right: 16px;
        }

        &.button-prev {
          top: 50%;
          left: 16px;
          transform: translateY(-50%);
        }

        &.button-next {
          top: 50%;
          right: 16px;
          transform: translateY(-50%);
        }

        &.disabled {
          color: #B2B7C2;
          cursor: not-allowed;
        }
      }
    }

    .main-swiper {
      width: 100%;
      aspect-ratio: 1 / 1;
      border-radius: 8px;

      .main-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 8px;
        user-select: none;
      }
    }
  }
}

// 缩略图
.thumbs-container {
  display: flex;
  align-items: center;

  .nav-btn {
    width: 16px;
    height: 70px;
    background-color: #F5F5F5;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #191E3B;
    cursor: pointer;
    &.disabled {
      color: #B2B7C2;
      cursor: not-allowed;
    }
    &.prev {
      margin-right: 8px;
    }
    &.next {
      margin-left: 8px;
    }
  }
  .thumbs-list {
    flex: 1;
    display: flex;
    align-items: center;
    height: 78px;
    overflow: scroll;
    scroll-behavior: smooth;
    &::-webkit-scrollbar {
      display: none;
    }

    .thumbs-item {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 66px;
      height: 66px;
      margin-right: 5px;
      cursor: pointer;

      @media (min-width: 1200px) {
        width: 78px;
        height: 78px;
      }

      &.active {
        border: 2px solid #052784;
        border-radius: 6px;
      }
      .img {
        height: 70px;
        width: 70px;
        border-radius: 4px;
        border: 1px solid #EBF1F5;
        user-select: none;
      }
    }
  }
}
</style>
